import { memo, useMemo, useRef } from 'react';
import { useTranslation } from '@frontend/i18n';
import { SurfaceHeader } from '@frontend/surface-header';
import { theme } from '@frontend/theme';
import { useModalControl, useTooltip } from '@frontend/design-system';
import { ChannelSettingsController } from '../../constants';
import { useTeamChatSelector } from '../../providers/team-chat.provider';
import { getUserFullName } from '../../utils';
import { ChatAvatar } from '../common/chat-avatar/chat-avatar';
import { HeaderExpandCollapseIcon } from '../header-expand-collapse-icon/header-expand-collapse-icon';
import { ConversationSettings } from './conversation-settings/conversation-settings';

export const ConversationTitle = memo(() => {
  const { t } = useTranslation('team-chat');
  const activeConversationId = useTeamChatSelector((ctx) => ctx.activeConversationId);
  const collapseNav = useTeamChatSelector((ctx) => ctx.collapseNav);
  const users = useTeamChatSelector((ctx) => ctx.users);
  const currentUser = useTeamChatSelector((ctx) => ctx.user);
  const isNavExpanded = useTeamChatSelector((ctx) => ctx.isNavExpanded);
  const helpers = useTeamChatSelector((ctx) => ctx.helpers);
  const closeConversation = useTeamChatSelector((ctx) => ctx.closeConversation);
  const selectedThreadParentId = useTeamChatSelector((ctx) => ctx.selectedThreadParentId);
  const closeTeamChat = useTeamChatSelector((ctx) => ctx.closeTeamChat);
  const closeThread = useTeamChatSelector((ctx) => ctx.closeThread);
  const isThread = selectedThreadParentId !== undefined;

  const { modalProps, openModal } = useModalControl();
  const initialTab = useRef<ChannelSettingsController>(ChannelSettingsController.about);
  const { Tooltip, tooltipProps, triggerProps } = useTooltip();
  const conversation = useMemo(
    () => (activeConversationId ? helpers.getConversation(activeConversationId) : undefined),
    [helpers, activeConversationId]
  );

  const name = conversation?.name || 'New Message';
  const type = conversation?.type || 'Group';

  const members = useMemo(
    () =>
      conversation?.memberIds
        .map((memberId) => users?.find((user) => user.userID === memberId))
        .filter((user) => !!user) ?? [],
    [conversation?.memberIds, users]
  );

  const allMembers = useMemo(
    () => [...(currentUser ? [currentUser] : []), ...members].filter((u) => !u.isDeactivated),
    [currentUser, members]
  );

  const conversationTitle = useMemo(() => {
    if (type === 'Group') {
      return name;
    } else if (type === 'DM') {
      if (members.length <= 3) {
        return members.map((member) => getUserFullName(member.firstName, member.lastName)).join(', ');
      } else {
        const membersLength = members.length - 2;
        return (
          members
            .slice(0, 2)
            .map((member) => getUserFullName(member.firstName, member.lastName))
            .join(', ') + t(', {{membersLength}} others', { membersLength })
        );
      }
    } else {
      return t('New Message');
    }
  }, [type, name, members, t]);

  const showMembersAsInitial = () => {
    initialTab.current = ChannelSettingsController.members;
    openModal();
  };

  const openDefaultModal = () => {
    initialTab.current = ChannelSettingsController.about;
    openModal();
  };

  const onClickBack = () => {
    if (isThread) {
      closeThread(conversation?.channelId || '');
    } else {
      closeConversation();
      if (isNavExpanded) {
        collapseNav();
      }
    }
  };

  return (
    <SurfaceHeader.Root>
      <SurfaceHeader.Row>
        {!isNavExpanded && (
          <SurfaceHeader.Start>
            <HeaderExpandCollapseIcon />
            <SurfaceHeader.Icon
              label='Back'
              onClick={onClickBack}
              iconName='caret-left-small'
              trackingId='team-chat-2.0-close-conversation-button'
            />
          </SurfaceHeader.Start>
        )}
        <SurfaceHeader.InteractiveButton
          onClick={openDefaultModal}
          trackingId='team-chat-2.0-open-channel-setting-with-about'
          {...triggerProps}
        >
          <SurfaceHeader.Title titlePostFixIcon={conversation && `caret-${modalProps.show ? 'up' : 'down'}-tiny`}>
            <div css={{ marginRight: theme.spacing(1) }}>{type === 'DM' ? <ChatAvatar users={members} /> : `#`}</div>
            <div css={[componentStyles.title]}>{conversationTitle}</div>
            {conversation && (
              <ConversationSettings
                conversationId={conversation.channelId}
                modalProps={modalProps}
                initialTab={initialTab.current}
              />
            )}
          </SurfaceHeader.Title>
        </SurfaceHeader.InteractiveButton>
        <SurfaceHeader.Actions>
          {(type === 'DM' ? members.length > 1 : members.length > 0) && (
            <>
              <SurfaceHeader.InteractiveButton
                onClick={showMembersAsInitial}
                trackingId='team-chat-2.0-open-channel-setting-with-members'
              >
                <ChatAvatar users={allMembers} size='xs' showGroupAvatars />
              </SurfaceHeader.InteractiveButton>
              <SurfaceHeader.Divider />
            </>
          )}
          {conversation?.description && <Tooltip {...tooltipProps}>{conversation.description}</Tooltip>}
          <SurfaceHeader.Icon iconName='x' onClick={closeTeamChat} trackingId='team-chat-2.0-close-team-chat-button' />
        </SurfaceHeader.Actions>
      </SurfaceHeader.Row>
    </SurfaceHeader.Root>
  );
});

ConversationTitle.displayName = 'ConversationTitle';

const componentStyles = {
  title: {
    maxWidth: '132px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};
